import { useState } from "react";

/**
 * Options for the useCommand hook.
 *
 * @template TData
 * @template TError
 */
interface UseCommandOptions<TData, TError> {
    /**
     * Callback function to be called on successful command execution.
     * Can return a Promise for asynchronous operations.
     */
    onSuccess?: (data: TData) => void | Promise<any>;
    /**
     * Callback function to be called on command execution error.
     */
    onError?: (data: TError) => void;
}

/**
 * State of the command execution.
 *
 * @template TData
 * @template TError
 */
interface UseCommandState<TData, TError> {
    /**
     * Indicates if the command is currently loading.
     */
    isLoading: boolean;
    /**
     * Indicates if there was an error during command execution.
     */
    isError: boolean;
    /**
     * Indicates if the command was executed successfully.
     */
    isSuccess: boolean;
    /**
     * The data returned from the command.
     */
    data: TData | null;
    /**
     * The error returned from the command.
     */
    error: TError | null;
}

/**
 * Result of the useCommand hook.
 *
 * @template TData
 * @template TError
 * @template TCommandParams
 */
interface UseCommandResult<TData, TError, TCommandParams> extends UseCommandState<TData, TError> {
    /**
     * Function to execute the command.
     */
    mutate: (variables: TCommandParams) => Promise<void>;
    /**
     * Function to reset the command state.
     */
    reset: () => void;
}

/**
 * Custom hook to manage command execution state.
 *
 * @template TData
 * @template TCommandParams
 * @template TError
 * @param {(variables: TCommandParams) => Promise<TData>} command - The command function to be executed.
 * @param {UseCommandOptions<TData, TError>} [options] - Optional callbacks for success and error handling.
 * @returns {UseCommandResult<TData, TError, TCommandParams>} The state and functions to manage command execution.
 */
function useCommand<TData, TCommandParams, TError = any>(
    command: (variables: TCommandParams) => Promise<TData>,
    options?: UseCommandOptions<TData, TError>
): UseCommandResult<TData, TError, TCommandParams> {
    const [state, setState] = useState<UseCommandState<TData, TError>>({
        isLoading: false,
        isError: false,
        isSuccess: false,
        data: null,
        error: null
    });

    const reset = (): void => setState(prevState => ({
        ...prevState,
        isLoading: true,
        isError: false,
        isSuccess: false,
        data: null,
        error: null
    }));

    const mutate = async (variables: TCommandParams): Promise<void> => {
        reset();

        try {
            const result = await command(variables);
            setState(prevState => ({
                ...prevState,
                isLoading: false,
                isSuccess: true,
                data: result
            }));
            if (options?.onSuccess) {
                await options.onSuccess(result);
            }
        }
        catch (err) {
            setState(prevState => ({
                ...prevState,
                isLoading: false,
                isError: true,
                error: err as TError
            }));
            if (options?.onError) {
                options.onError(err as TError);
            }
        }
    };

    return {
        ...state,
        mutate,
        reset
    };
}

export {
    useCommand,
    UseCommandOptions,
    UseCommandResult,
    UseCommandState
};
